import { renderPrincipal } from "@/shared/components/ResourceView";
import {
  cellStyles,
  createColumnHelper,
  renderDashOr,
  renderDashOrValue,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { nanoSecToDate } from "@/shared/time";

import { EventRecord } from "./EventRecord";

const columnHelper = createColumnHelper<EventRecord>();

/**
 * Normalize a nanosecond event time into a `Date` for `TimestampCell`, or
 * `null` (→ em dash) when absent. The column owns this conversion because it
 * knows the field is nanosecond `bigint`; the cell only speaks instants.
 */
function nanosToDateOrNull(
  ns: bigint | number | undefined | null,
): Date | null {
  if (ns === undefined || ns === null) {
    return null;
  }
  return nanoSecToDate(BigInt(ns));
}

export const eventsTableColumns = [
  columnHelper.accessor("event_id", {
    headerName: "ID",
    width: 180,
    cellClass: cellStyles.idCell,
  }),
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 3,
    sortable: true,
    cellClass: cellStyles.primaryContent,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor("description", {
    headerName: "Description",
    flex: 3,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.timestamp("start_time", {
    headerName: "Start",
    width: 120,
    sortable: true,
    variant: "precise",
    valueGetter: (p) => nanosToDateOrNull(p.data?.start_time),
  }),
  columnHelper.timestamp("end_time", {
    headerName: "End",
    width: 120,
    sortable: true,
    variant: "precise",
    valueGetter: (p) => nanosToDateOrNull(p.data?.end_time),
  }),
  columnHelper.timestamp("created", {
    headerName: "Created",
    width: 150,
    sortable: true,
    variant: "relative",
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    width: 150,
    sortable: true,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.timestamp("modified", {
    headerName: "Modified",
    width: 150,
    sortable: true,
    variant: "relative",
  }),
  columnHelper.accessor("modified_by", {
    headerName: "Modified by",
    width: 150,
    sortable: true,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 2,
    cellClass: cellStyles.tagsCell,
    // Object types require a string formatting for accessibility
    valueFormatter: (p) => (p.value ?? []).join(", "),
    cellRenderer: renderDashOr(
      (p: ICellRendererParams<EventRecord, string[]>) => (
        <TagGroup tags={p.value ?? []} maxDisplayCount={3} />
      ),
    ),
  }),
];
